import { useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CmsImage } from '@/components/CmsImage';
import { useStoreRedirect } from '@/hooks/useStoreRedirect';
import { getRedirectTrackingAttrs } from '@/lib/pageClickUtils';
import { buildSubPageHref } from '@/lib/pageRedirectUtils';
import { PageRedirectConfig, PageResponse } from '@/lib/types';
import { cn, preserveQueryParams } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { getTabStateClass } from './MenuItem';
import { StyledMenuScope, useMenuScopeProps } from './menuStyle';
import { MenuSubItem } from './MenuSubItem';
import { ChevronDown } from 'lucide-react';

interface ITopbarMenuItemProps {
  title: string;
  dataPriority: number;
  dataTestId: string;
  to?: string;
  subPages?: PageResponse[];
  isDisabled?: boolean;
  isTopFixedHeader?: boolean;
  redirectConfig?: PageRedirectConfig;
}

export const TopbarMenuItem = ({
  dataPriority,
  dataTestId,
  title,
  to,
  subPages = [],
  isDisabled = false,
  isTopFixedHeader = false,
  redirectConfig,
}: ITopbarMenuItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { initialBoot, setCurrentPageName } = useAppState();
  const { redirect } = useStoreRedirect();
  const isSubPage = subPages.length > 0;
  const menuScopeProps = useMenuScopeProps();
  const pathname = usePathname();
  const isActive = pathname === to;
  const isRedirect = Boolean(redirectConfig?.enabled && redirectConfig.redirectUrl);

  const href = isDisabled || isRedirect ? '' : preserveQueryParams(to || '/');

  const handleClick = (e: React.MouseEvent) => {
    if (isRedirect) {
      e.preventDefault();
      redirect(redirectConfig!.redirectUrl, redirectConfig!.isStoreRedirect);
      return;
    }
    setCurrentPageName(title);
  };

  const handleLinkMouseEnter = () => {
    setIsHovered(true);
    setIsExpanded(true);
  };

  const handleLinkMouseLeave = (e: React.MouseEvent) => {
    setIsHovered(false);
    const relatedTarget = e.relatedTarget as HTMLElement;
    const isMovingToPopover = relatedTarget?.closest('[role="dialog"]') !== null;

    if (!isMovingToPopover) {
      setIsExpanded(false);
    }
  };

  const handlePopoverMouseEnter = () => {
    setIsExpanded(true);
  };

  const handlePopoverMouseLeave = () => {
    setIsExpanded(false);
  };

  if (!isSubPage) {
    return (
      <Link
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-priority={dataPriority}
        data-testid={dataTestId}
        {...getRedirectTrackingAttrs(isRedirect, redirectConfig)}
        href={href}
        onClick={handleClick}
        className={cn(
          `menu-item flex h-full max-w-[200px] shrink-0 cursor-pointer items-center justify-between gap-2 rounded-lg p-0 text-xl outline-none focus:outline-none`,
          isTopFixedHeader ? (isActive ? 'font-black' : 'font-medium') : 'font-bold',
          getTabStateClass(isActive, isHovered)
        )}
      >
        <div className="menu-label min-w-0 truncate">{title}</div>
      </Link>
    );
  }

  return (
    <Popover open={isExpanded} onOpenChange={setIsExpanded}>
      <PopoverTrigger asChild>
        <Link
          onMouseEnter={handleLinkMouseEnter}
          onMouseLeave={handleLinkMouseLeave}
          data-priority={dataPriority}
          data-testid={dataTestId}
          {...getRedirectTrackingAttrs(isRedirect, redirectConfig)}
          href={href}
          onClick={handleClick}
          className={cn(
            `menu-item group flex h-full max-w-[200px] shrink-0 cursor-pointer items-center justify-between gap-2 rounded-lg px-2 text-xl outline-none focus:outline-none`,
            isTopFixedHeader ? (isActive ? 'font-black' : 'font-medium') : 'font-bold',
            getTabStateClass(isActive, isHovered)
          )}
        >
          <div className="menu-label min-w-0 truncate">{title}</div>
          <ChevronDown
            className={cn(
              'size-6 min-h-6 min-w-6 transition-transform duration-300 ease-in-out',
              isExpanded && '-rotate-180'
            )}
          />
        </Link>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
        style={{
          background: initialBoot?.theme?.header?.backgroundColor?.colorOne || '#FFF',
        }}
        className="-mt-2 w-[224px] rounded-b-xl border-none p-2.5"
      >
        <StyledMenuScope {...menuScopeProps} className="menu-sub-items flex flex-col">
          {subPages.map((subPage) => (
            <MenuSubItem
              key={subPage.id}
              data-testid={`PortalSidebar-SubItem-Link-${subPage.slug}`}
              to={buildSubPageHref(to, subPage.slug)}
              title={subPage.pageName}
              icon={
                subPage.pageIcon ? (
                  <CmsImage
                    src={subPage.pageIcon}
                    alt={subPage.pageTemplate}
                    width={24}
                    height={24}
                    className="menu-icon size-6 shrink-0"
                  />
                ) : null
              }
              onClick={() => setIsExpanded(false)}
              isTopFixedHeader={isTopFixedHeader}
              className="px-2.5 font-normal"
            />
          ))}
        </StyledMenuScope>
      </PopoverContent>
    </Popover>
  );
};
